import React from 'react';
import { ObservableQuery } from '@apollo/client';
import classnames from 'classnames/bind';

import ArchiveThreadButton from '@buyside/components/presentation/MessageThreadIndexPage/MessageThreadIndex/archiveThreadButton';
import Card from '@buyside/components/base/Card/Card';
import { IndexTitle } from '@buyside/components/presentation/IndexPage/IndexPage';
import MessageCard from '../MessageCard/MessageCard';
import MessageIndexPaginationButton from '@buyside/components/presentation/MessagesIndexPage/MessageIndexPaginationButton/MessageIndexPaginationButton';
import MessageThreadForm from './MessageThreadForm';
import PaginatedIndex, {
  PaginatedIndexItems
} from '@buyside/components/presentation/Index/paginatedIndex';

import {
  MarkMessageAsReadMutationFn,
  MessageThreadItemFragment,
  ThreadMessagesQuery,
  ThreadMessagesQueryVariables,
  CreateMessageForThreadMutationFn,
  UpdateIntroRequestStatusMutationFn
} from '@interfaces/graphql-typings';

import styles from './MessageThreadIndexStyles.scss';
const cx = classnames.bind(styles);

interface ThreadUserProps {
  user: MessageThreadItemFragment['otherParty'];
}

interface CreateMessageFormProps {
  threadNum: number;
  createMessage: CreateMessageForThreadMutationFn;
  isArchived: MessageThreadItemFragment['archiveStatus'];
}

interface MessageThreadIndexProps {
  threadNum: number;
  createMessage: CreateMessageForThreadMutationFn;
  isArchived?: boolean;
  fetchPreviousPage: ObservableQuery<
    ThreadMessagesQuery,
    ThreadMessagesQueryVariables
  >['fetchMore'];
  hasPreviousPage: boolean;
  markMessageAsRead: MarkMessageAsReadMutationFn;
  messages: MessageThreadItemFragment[];
  updateIntroRequest: UpdateIntroRequestStatusMutationFn;
  currentUserId: string;
}

const employerName = ({ user }: ThreadUserProps): string => {
  if (!user) return '';
  if (user.currentEmployerName) {
    return `(currently at ${user.currentEmployerName})`;
  } else if (user.mostRecentEmployer && user.mostRecentEmployer.employer) {
    return `(formerly at ${user.mostRecentEmployer.employer.name})`;
  } else {
    return '';
  }
};

const CreateMessageForm: React.FunctionComponent<CreateMessageFormProps> = ({
  threadNum,
  createMessage,
  isArchived
}): React.ReactElement => (
  <Card>
    {({ cardStyle }: { cardStyle: string }): React.ReactElement => {
      const className = cx({
        cardContainer: true,
        cardStyle: cardStyle
      });
      return (
        <div className={className}>
          <MessageThreadForm
            analyticsTag="send-reply-message"
            createMessage={createMessage}
            threadNum={threadNum}
          />
          <hr className={styles.divider} />
          <ArchiveThreadButton threadNum={threadNum.toString()} isArchived={Boolean(isArchived)} />
        </div>
      );
    }}
  </Card>
);

const MessageThreadIndex: React.FunctionComponent<MessageThreadIndexProps> = ({
  createMessage,
  fetchPreviousPage,
  hasPreviousPage,
  markMessageAsRead,
  messages,
  threadNum,
  updateIntroRequest,
  currentUserId
}): React.ReactElement => {
  const latestMessage = messages[messages.length - 1];
  if (latestMessage.readStatus === false) {
    markMessageAsRead({ variables: { id: latestMessage.id } });
  }

  const messageRenderer = (message: MessageThreadItemFragment, idx: string): React.ReactElement => {
    return (
      <MessageCard
        message={message}
        key={idx}
        updateIntroRequest={updateIntroRequest}
        currentUserId={currentUserId}
      />
    );
  };

  const rootCapIntroMessage = messages.at(0)?.introRequest?.status !== 'pending';
  const hasOtherParty = Boolean(messages[0]?.otherParty);

  return (
    <>
      <IndexTitle>{messages[0].subject}</IndexTitle>
      {rootCapIntroMessage && (
        <p className={styles.threadSubtitle}>
          Conversation with: {hasOtherParty ? messages[0].otherParty?.displayName : 'Former User'}{' '}
          {hasOtherParty ? employerName({ user: messages[0].otherParty }) : null}
        </p>
      )}
      <PaginatedIndex
        fetchNextPage={fetchPreviousPage}
        hasNextPage={hasPreviousPage}
        indexItems={messages}
        indexItemRenderer={messageRenderer}
        updateIntroRequest={updateIntroRequest}
      >
        <MessageIndexPaginationButton />
        <PaginatedIndexItems className={styles.paginatedItems} />
      </PaginatedIndex>
      {rootCapIntroMessage && hasOtherParty && (
        <CreateMessageForm
          createMessage={createMessage}
          isArchived={latestMessage.archiveStatus}
          threadNum={threadNum}
        />
      )}
    </>
  );
};

export default MessageThreadIndex;
